/**
 * Pure step-machine for the guided first-story coach tour — LANE STORY-TOUR.
 * Jason's spec, verbatim: "a clickable do this, do that... Welcome to Mira ->
 * follow these instructions -> click here to do this, then here, then here ->
 * then we'll do this together: tell me about your story... about six clicks
 * to get the whole story." Kept framework-free (no React, no fetch) so the
 * advance/skip/complete transitions and each step's anchor id are directly
 * unit-testable, same house idiom onboardingStep.ts/quickStartGuide.ts/
 * jobBuckets.ts/dragRehome.ts already establish for this app's pure modules
 * (this repo has no jsdom/React-Testing-Library setup).
 *
 * THIS IS DELIBERATELY A SEPARATE SYSTEM FROM Onboarding.tsx/onboardingStep.ts
 * (the full-screen modal wizard — demo card / one-question capture /
 * interview handoff / first-output generate / watchlist-seed, gated on
 * server-truth roleCount) and FROM QuickStartGuide.tsx/quickStartGuide.ts (the
 * dismissible checklist Card on Home with deep-links, no anchoring). Neither
 * of those points at a REAL on-screen control in place — this module is the
 * click-coached tour the mission asks for: a scrim + a highlighted real
 * control + a step card, riding ON TOP of the actual screens as the person
 * navigates them for real, one real click at a time. See CoachMark.tsx's own
 * header for the compose-not-evolve call and why.
 *
 * Seven steps — Jason's original six-click flow plus the resume-first step his
 * 2026-07-20 walk ordered ("we need the tour to get the resume and ask for it
 * if empty"): the tour now walks the person PAST the paste box before the
 * first spoken story, so Meira never opens a conversation about material she
 * was never given.
 *   1. welcome            -> no anchor (a centered card, first paint)
 *   2. home-orientation   -> the readiness/story-score card on Home
 *   3. get-to-refine      -> the Refine tab (bottom bar) or the nav trigger
 *   4. paste-resume       -> the paste-your-resume card on Refine (his 7/20 walk)
 *   5. first-story        -> the mic button on Refine
 *   6. see-it-land        -> the Cards / Whole résumé toggle on Refine
 *   7. where-to-next      -> no anchor (a centered card pointing at Jobs/Get Found)
 */

export type StoryTourStep =
  | 'welcome'
  | 'home-orientation'
  | 'get-to-refine'
  | 'paste-resume'
  | 'first-story'
  | 'see-it-land'
  | 'where-to-next';

export const STORY_TOUR_STEPS: StoryTourStep[] = [
  'welcome',
  'home-orientation',
  'get-to-refine',
  'paste-resume',
  'first-story',
  'see-it-land',
  'where-to-next',
];

/** The `data-tour="…"` value each step highlights on the real DOM — the
 *  contract between this pure module and the real controls it points at
 *  (TabBar's Refine tab, VoiceTextarea's mic button, Refine's Cards/résumé
 *  toggle, Home's story-score card). `null` means the step renders as a
 *  centered card with no cutout (welcome/where-to-next — neither one is
 *  coaching a specific control, per the mission's own step list). */
export const STORY_TOUR_ANCHOR: Record<StoryTourStep, string | null> = {
  welcome: null,
  'home-orientation': 'home-story-score',
  'get-to-refine': 'tab-refine',
  'paste-resume': 'refine-paste-box',
  'first-story': 'refine-mic-button',
  'see-it-land': 'refine-view-toggle',
  'where-to-next': null,
};

/** Which route each step's anchor lives on — read by the host so a step
 *  whose anchor lives on a DIFFERENT route than the one currently mounted
 *  still renders its step card honestly (a "head to Refine" nudge) rather
 *  than silently failing to find the anchor in the DOM — see CoachMark.tsx's
 *  own doc comment for how a missing-anchor frame is handled, never a crash. */
export const STORY_TOUR_ROUTE: Record<StoryTourStep, '/' | '/refine'> = {
  welcome: '/',
  'home-orientation': '/',
  'get-to-refine': '/',
  'paste-resume': '/refine',
  'first-story': '/refine',
  'see-it-land': '/refine',
  'where-to-next': '/refine',
};

/** Steps that COMPLETE by the person arriving somewhere rather than by a
 *  click on the anchored control itself — his 2026-07-20 walk receipt: the
 *  "Let's go to Refine" card was still sitting there AFTER he'd landed on
 *  Refine (he'd navigated some way other than tapping the ringed tab, so the
 *  click-piggyback never fired). Maps each such step to the pathname whose
 *  arrival advances it; null = arrival means nothing to this step. Read by
 *  resolveArrivalAdvance below — kept as data + a pure function so the
 *  "arriving there advances it" rule is unit-testable without a DOM. */
export const STORY_TOUR_ARRIVAL: Record<StoryTourStep, string | null> = {
  welcome: null,
  'home-orientation': null,
  'get-to-refine': '/refine',
  'paste-resume': null,
  'first-story': null,
  'see-it-land': null,
  'where-to-next': null,
};

/** True when the given step should auto-advance because the person has
 *  genuinely arrived at the pathname the step was coaching them toward.
 *  Pure — the caller (CoachMark's host effect) owns the actual advance. */
export function shouldAdvanceOnArrival(step: StoryTourStep, pathname: string): boolean {
  const arrival = STORY_TOUR_ARRIVAL[step];
  return arrival !== null && pathname === arrival;
}

function stepIndex(step: StoryTourStep): number {
  return STORY_TOUR_STEPS.indexOf(step);
}

export interface StoryTourState {
  /** Index into STORY_TOUR_STEPS of the step currently showing. -1 means the
   *  tour is not active (never started, or completed/dismissed) — mirrors
   *  onboardingStep.ts's 'done' sentinel but as an index so `advance`
   *  arithmetic stays simple; resolveStoryTourStep below is what a caller
   *  actually reads to render (or not render) the overlay. */
  stepIdx: number;
  /** Explicit "I'll explore on my own" / [Skip tour] — retires the tour
   *  outright, same one-way-door semantics as onboardingStep.ts's skipped. */
  dismissed: boolean;
  /** All six steps reached in order at least once — distinct from `dismissed`
   *  so a completed run and an abandoned run can be told apart if that ever
   *  matters (e.g. a future "you finished the tour" analytics signal); today
   *  both read identically to resolveStoryTourStep (neither renders). */
  completed: boolean;
}

export const INITIAL_STORY_TOUR_STATE: StoryTourState = {
  stepIdx: -1,
  dismissed: false,
  completed: false,
};

/** Start (or restart) the tour from step 1 — used both for a genuine first
 *  run and for Settings' "Show me around" manual re-run row. Clears
 *  dismissed/completed so a re-run is a real, full run, never a no-op. */
export function startStoryTour(_state: StoryTourState): StoryTourState {
  return { stepIdx: 0, dismissed: false, completed: false };
}

/** Advance to the next step, or complete the tour from the final step.
 *  Never advances past the end — calling this on 'where-to-next' completes.
 *  A no-op (returns state unchanged) if the tour is not currently active. */
export function advanceStoryTour(state: StoryTourState): StoryTourState {
  if (state.stepIdx < 0) return state;
  const next = state.stepIdx + 1;
  if (next >= STORY_TOUR_STEPS.length) {
    return { ...state, stepIdx: -1, completed: true };
  }
  return { ...state, stepIdx: next };
}

/** Jump directly to a named step — used when the real anchored control is
 *  clicked (e.g. tapping the actual Refine tab on step 3 should land on step
 *  4, not merely increment by one). Clamps to a valid step; a step not in the
 *  tour, or calling this while inactive, is a no-op (returns state
 *  unchanged) rather than landing on an arbitrary index. */
export function goToStoryTourStep(state: StoryTourState, step: StoryTourStep): StoryTourState {
  if (state.stepIdx < 0) return state;
  const idx = stepIndex(step);
  if (idx < 0) return state;
  return { ...state, stepIdx: idx };
}

/** The explicit skip — [Skip tour] / Esc / "I'll explore on my own". Retires
 *  the overlay regardless of which step it was on. One-way, matches
 *  onboardingStep.ts's skipOnboarding. */
export function skipStoryTour(state: StoryTourState): StoryTourState {
  return { ...state, stepIdx: -1, dismissed: true };
}

/** The step to render, or null (render nothing). Read by CoachMark's host —
 *  never renders on a negative or out-of-range index. */
export function resolveStoryTourStep(state: StoryTourState): StoryTourStep | null {
  if (state.stepIdx < 0) return null;
  return STORY_TOUR_STEPS[state.stepIdx] ?? null;
}

/**
 * Whether the tour is eligible to auto-start for this account at all —
 * reuses useOnboardingGate's own corpus-derived "no roles yet" server-truth
 * signal (the SAME GET /corpus/roles read, never a second corpus fetch of
 * its own — see useStoryTour.ts) so a first-run-shaped account is defined
 * identically everywhere in this app, not by a second, potentially drifting
 * definition. `roleCount` null (still loading / unknown) is NOT eligible yet
 * — matches this app's "unknown, not empty" discipline; the caller simply
 * waits for the next render once the read resolves, exactly as
 * useOnboardingGate's own resolveOnboardingStep already does.
 */
export function isStoryTourEligible(roleCount: number | null): boolean {
  return roleCount === 0;
}

// =============================================================================
// localStorage persistence — v1 is client-only (mission's own honest note:
// multi-device resume is a later preferences-jsonb ride). Mirrors
// onboardingStep.ts/quickStartGuide.ts's hasWindow()/try-catch discipline
// exactly: a locked-down/private-mode/non-browser context degrades to "never
// seen, never dismissed" rather than throwing.
// =============================================================================

const STORAGE_KEY = 'tl-story-tour';

export interface StoredStoryTour {
  /** Has this account EVER seen the tour trigger (auto-start OR manual
   *  re-run)? Distinct from `dismissed` — a person who dismissed step 1 has
   *  both seen:true and dismissed:true; a person who never reached the
   *  first-run screen at all has seen:false. Read by useStoryTour's
   *  auto-start gate so a first-run-shaped account only ever auto-starts
   *  ONCE, even across reloads mid-tour. */
  seen: boolean;
  /** The step index at last write — resumes a reload mid-tour at the same
   *  step, rather than restarting from 'welcome' (mirrors onboardingStep.ts's
   *  own resumable furthestStepIndex spirit, though this tour is short enough
   *  that resuming at the LAST step shown, not the furthest, is the more
   *  honest "pick up where you left off" for a coach-mark walkthrough). -1
   *  when not currently mid-tour. */
  stepIdx: number;
  dismissed: boolean;
  completed: boolean;
}

const INITIAL_STORED: StoredStoryTour = { seen: false, stepIdx: -1, dismissed: false, completed: false };

function hasWindow(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function readStoryTourStorage(): StoredStoryTour {
  if (!hasWindow()) return INITIAL_STORED;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL_STORED;
    const parsed = JSON.parse(raw) as Partial<StoredStoryTour>;
    const stepIdx =
      typeof parsed.stepIdx === 'number' && parsed.stepIdx >= -1 && parsed.stepIdx < STORY_TOUR_STEPS.length
        ? parsed.stepIdx
        : -1;
    return {
      seen: parsed.seen === true,
      stepIdx,
      dismissed: parsed.dismissed === true,
      completed: parsed.completed === true,
    };
  } catch {
    return INITIAL_STORED;
  }
}

export function writeStoryTourStorage(stored: StoredStoryTour): void {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Best-effort only, same posture as onboardingStep.ts's writeOnboardingProgress
    // — worst case the tour re-triggers next session instead of staying dismissed.
  }
}
